import Vue from 'vue';
import publicationsService from '../../api/publications-service';
import imageService from '../../api/image-service';
import { removeArrayItem, updateArrayItem } from '../../util/array';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '../../util/event-bus';
import i18n from '../../i18n/i18n-config';
import { mapErrorsToInputs } from '../../util/forms';

const state = {
  publications: null,
  publicationsPagination: {},
  editedPublication: {},
  publicationValidationErrors: {},
  publicationFilterParams: {},
  loadingPublications: false,
  savingPublication: false,
  savingImage: false,
};

const mutations = {
  SET_PUBLICATIONS(state, { data, current_page, per_page, total }) {
    state.publications = data;
    state.publicationsPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_FILTER_PARAMS(state, params) {
    state.publicationFilterParams = params;
  },

  STORE_PUBLICATION(state, publication) {
    state.publications = [...state.publications, publication];
    state.publicationValidationErrors = {};
    state.publicationsPagination.total += 1;
  },

  SET_EDITED_PUBLICATION(state, publication) {
    state.publicationValidationErrors = {};
    state.editedPublication = JSON.parse(JSON.stringify(publication));
  },

  UPDATE_PUBLICATION(state, publication) {
    state.publications = updateArrayItem(state.publications, publication);
  },

  DELETE_PUBLICATION(state, publication) {
    state.publications = removeArrayItem(state.publications, publication);
    state.publicationsPagination.total -= 1;
  },

  SET_PUBLICATION_VALIDATION_ERRORS(state, validationErrors) {
    state.publicationValidationErrors = validationErrors;
  },

  CLEAR_VALIDATION_ERRORS(state, field) {
    delete state.publicationValidationErrors[field];
  },

  SET_LOADING_PUBLICATIONS(state, loading) {
    state.loadingPublications = loading;
  },

  SET_SAVING_PUBLICATION(state, saving) {
    state.savingPublication = saving;
  },

  SET_SAVING_IMAGE(state, saving) {
    state.savingImage = saving;
  },

  SET_PUBLICATIONS_IMAGE(state, image) {
    Vue.set(state.editedPublication, 'image', image);
    for (let i = 0; i < state.publications?.length; i++) {
      if (state.publications[i].id === +image.model_id) {
        Vue.set(state.publications[i], 'image', image);
        break;
      }
    }
  },

  DELETE_PUBLICATIONS_IMAGE(state, image) {
    Vue.set(state.editedPublication, 'image', null);
    for (let i = 0; i < state.publications?.length; i++) {
      if (state.publications[i].id === +image.model_id) {
        Vue.set(state.publications[i], 'image', null);
        break;
      }
    }
  },
};

const actions = {
  fetchPublications({ commit }, params) {
    commit('SET_LOADING_PUBLICATIONS', true);
    return publicationsService
      .getPage(params)
      .then((res) => {
        commit('SET_PUBLICATIONS', res.data);
        if (params.page !== state.publicationFilterParams.page) {
          window.scrollTo(0, 0);
        }
        commit('SET_FILTER_PARAMS', params);
      })
      .finally(() => {
        commit('SET_LOADING_PUBLICATIONS', false);
      });
  },

  async storePublication({ commit }) {
    commit('SET_SAVING_PUBLICATION', true);
    const res = await publicationsService.create();
    commit('SET_SAVING_PUBLICATION', false);
    commit('STORE_PUBLICATION', res.data);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('publication_created'));
    return res;
  },

  editPublication({ state, commit }, publicationId) {
    const publication = state.publications?.find((p) => p.id === publicationId);
    if (publication) {
      commit('SET_EDITED_PUBLICATION', publication);
      return Promise.resolve(publication);
    }
    return publicationsService.getById(publicationId).then((res) => {
      commit('SET_EDITED_PUBLICATION', res.data);
    });
  },

  updatePublication({ commit }, publication) {
    commit('SET_SAVING_PUBLICATION', true);
    return publicationsService
      .update(publication)
      .then((res) => {
        commit('UPDATE_PUBLICATION', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('publication_updated'));
      })
      .catch((err) => {
        commit('SET_PUBLICATION_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      })
      .finally(() => commit('SET_SAVING_PUBLICATION', false));
  },

  deletePublication({ commit }, publication) {
    openConfirmDialog({
      title: i18n.t('confirm_publication_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      publicationsService.delete(publication).then(() => {
        commit('DELETE_PUBLICATION', publication);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('publication_deleted'));
      });
    });
  },

  clearPublicationValidationErrors({ commit }, field) {
    commit('CLEAR_VALIDATION_ERRORS', field);
  },

  async uploadPublicationsImage({ commit }, { image, type, modelType, modelId }) {
    commit('SET_SAVING_IMAGE', true);
    eventBus.$emit(OPEN_SNACKBAR, {
      text: i18n.t('uploading'),
      showProgress: true,
      timeout: -1,
    });
    const res = await imageService.upload(image, type, modelType, modelId);
    commit('SET_SAVING_IMAGE', false);
    commit('SET_PUBLICATIONS_IMAGE', res.data[0]);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('image_uploaded'));
    return res;
  },

  deletePublicationsImage({ commit }, image) {
    openConfirmDialog({
      title: i18n.t('confirm_publications_image_delete'),
    }).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      imageService.delete(image).then(() => {
        commit('DELETE_PUBLICATIONS_IMAGE', image);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('image_deleted'));
      });
    });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
